import {type PartialOptions, UI} from "../../../stem-core/ui/UIBase.js";
import {ConfirmationModal} from "../../../blinkpay/ui/ConfirmationModal.jsx";
import {SubscriptionOffer} from "../../../client/state/SubscriptionOfferStore.js";
import {BlinkNumberInput} from "../Base.js";
import {StemDate} from "../../../stem-core/time/Date.js";
import {InputLabel} from "../../../core/ui/input/InputLabel.jsx";
import {SubscriptionOfferSelectInput} from "../input/SubscriptionOfferSelectInput.jsx";
import {SubscriptionBenefitsEditor} from "../../pages/subscriptions/SubscriptionBenefitsEditor.jsx";
import {CheckboxInput} from "../../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import type {MerchantUser} from "../../../client/state/merchant/MerchantUserStore";

interface MerchantGrantSubscriptionModalOptions {
    merchantUser?: MerchantUser;
}

export class MerchantGrantSubscriptionModal extends ConfirmationModal<MerchantGrantSubscriptionModalOptions> {
    declare customBenefitsCheckbox: CheckboxInput;
    declare daysInput: BlinkNumberInput;
    declare extraBenefitsEditor: SubscriptionBenefitsEditor;
    declare hoursInput: BlinkNumberInput;
    declare monthsInput: BlinkNumberInput;
    declare subscriptionOfferSelector: SubscriptionOfferSelectInput;

    getDefaultOptions(): PartialOptions<MerchantGrantSubscriptionModal> {
        return {
            ...super.getDefaultOptions(),
            title: "Grant a subscription",
            description: "Manually grant the user a subscription." +
                " No payment will be charged, and the user will have to add one to continue having the subscription" +
                " (unless you manually extend it of course).",
            confirmLabel: "Create subscription",
            confirmAction: () => this.grantSubscription(),
            merchantUser: null,
        }
    }

    getSubscriptionPaidUntil() {
        const currentPaidUntil = StemDate.now();
        const addedHours = this.hoursInput?.getValue() || 0;
        const addedDays = this.daysInput?.getValue() || 0;
        const addedMonths = this.monthsInput?.getValue() || 0;

        return currentPaidUntil.add({hours: addedHours, days: addedDays, months: addedMonths});
    }

    getSelectedSubscriptionOffer() {
        return this.subscriptionOfferSelector?.getValue();
    }

    // TODO Not sure I like the verbosity here
    updateConfirmButton() {
        const disabled = !this.getSelectedSubscriptionOffer();
        if (disabled !== this.confirmButtonDisabled) {
            this.confirmButtonDisabled = disabled;
            this.redraw();
        }
    }

    render() {
        const {merchantUser} = this.options;
        const subscriptionOffers = SubscriptionOffer.all();
        const inputOptions = {
            onChange: () => this.redraw(),
            style: {
                width: 120,
                display: "inline-block",
            }
        };

        return [
            <div>User: {merchantUser.getName()} ({merchantUser.getEmail()})</div>,
            <InputLabel label="Subscription Plan"/>,
            <SubscriptionOfferSelectInput ref="subscriptionOfferSelector"
                                          offers={subscriptionOffers}
                                          onChange={() => this.updateConfirmButton()}/>,
            <div>
                <BlinkNumberInput ref="hoursInput" {...inputOptions} /> hours to add.
            </div>,
            <div>
                <BlinkNumberInput ref="daysInput" {...inputOptions} /> days to add.
            </div>,
            <div>
                <BlinkNumberInput ref="monthsInput" {...inputOptions} /> months to add
            </div>,
            <div>
                Subscription marked as paid until {this.getSubscriptionPaidUntil()}.
            </div>,
            <div>
                <CheckboxInput
                    ref="customBenefitsCheckbox"
                    label="Grant extra benefits beyond the selected offer"
                    onChange={() => this.redraw()}
                />
            </div>,
            this.customBenefitsCheckbox?.getValue() && <SubscriptionBenefitsEditor ref="extraBenefitsEditor"/>,
        ]
    }

    onMount() {
        super.onMount();
        this.updateConfirmButton();
        this.subscriptionOfferSelector.focus();
    }

    getExtraBenefitIds() {
        if (!this.customBenefitsCheckbox?.getValue()) {
            return [];
        }
        return this.extraBenefitsEditor?.getValue() || [];
    }

    async grantSubscription() {
        const request = {
            userId: this.options.merchantUser.userId,
            subscriptionOfferId: this.getSelectedSubscriptionOffer().id,
            paidUntil: this.getSubscriptionPaidUntil(),
            extraBenefitIds: this.getExtraBenefitIds(),
        }

        await this.makeRequest("/merchant/grant_subscription", request);
    }
}
